/**
 * Eventos mapeados da aplicação
 * Todos os eventos de tracking devem ser definidos aqui para manter a consistência
 */

export enum EventType {
  CLICK = 'click',
  VIEW = 'view',
  SUBMIT = 'submit',
  ERROR = 'error',
  TRANSACTIONAL = 'transacional',
}

/**
 * Interface base para payloads de eventos
 */
export interface BaseEventPayload {
  idMedico: number;
  idParceiro: number;
  isMobile: boolean;
  timestamp: string;
}

/**
 * Evento: Início da Gravação
 * Mede quantas vezes o médico tenta usar a funcionalidade e se a permissão do microfone foi concedida
 */
export interface PrescriptionVoiceBeginPayload extends BaseEventPayload {
  permissionStatus: 'allowed' | 'denied';
}

/**
 * Evento: Onboarding
 * Rastreia o progresso do médico pelo onboarding
 */
export interface PrescriptionVoiceOnboardingPayload extends BaseEventPayload {
  onboardingStep: 'step1' | 'step2' | 'final';
}

/**
 * Evento: Encerramento da Gravação
 * Rastreia a conclusão da gravação
 */
export interface PrescriptionVoiceStopPayload extends BaseEventPayload {
  stopReason: 'manual' | 'timeout';
}

/**
 * Evento: Botão de Mudo
 * Registra ação de mutar/desmutar
 */
export interface PrescriptionVoiceMutePayload extends BaseEventPayload {
  action: 'mute' | 'unmute';
}

/**
 * Evento: Cancelamento
 * Rastreia cancelamento da gravação
 */
export interface PrescriptionVoiceCancelPayload extends BaseEventPayload {
  cancelReason: 'userCancel' | 'closeWindow';
}

/**
 * Evento: Toggle Janela
 * Rastreia minimizar/maximizar
 */
export interface PrescriptionVoiceWindowTogglePayload extends BaseEventPayload {
  action: 'minimize' | 'maximize';
}

/**
 * Evento: Transcrição Completa
 * Marca conclusão do processo de transcrição
 */
export interface PrescriptionVoiceTranscriptionSuccessPayload extends BaseEventPayload {
  status: 'complete' | 'incomplete';
  hasPosology: boolean;
}

/**
 * Evento: Posologia Incompleta
 * Monitora interação com prescrição incompleta
 */
export interface PrescriptionIncompletePosologyPayload extends BaseEventPayload {
  idPrescription: number;
}

/**
 * Mapa de eventos da aplicação
 * Cada evento deve ter um nome único e um tipo
 */
export const TRACKED_EVENTS = {
  PRESCRIPTION_VOICE_BEGIN: {
    name: 'prescription_voice_begin_btn_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_ONBOARDING_NEXT: {
    name: 'prescription_voice_onboarding_next_btn_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_STOP: {
    name: 'prescription_voice_stop',
    type: EventType.TRANSACTIONAL,
  },
  PRESCRIPTION_VOICE_RETRY_UPLOAD: {
    name: 'prescription_voice_retry_upload_btn_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_MICROPHONE_MENU: {
    name: 'prescription_voice_microphone_menu_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_MUTE: {
    name: 'prescription_voice_mute_btn_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_CANCEL: {
    name: 'prescription_voice_cancel_btn_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_CANCEL_CONFIRM: {
    name: 'prescription_voice_cancel_confirm_btn_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_CANCEL_DENY: {
    name: 'prescription_voice_cancel_deny_btn_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_WINDOW_TOGGLE: {
    name: 'prescription_voice_window_toggle_btn_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_TRANSCRIPTION_SUCCESS: {
    name: 'prescription_voice_transcription_success',
    type: EventType.TRANSACTIONAL,
  },
  PRESCRIPTION_INCOMPLETE_POSOLOGY_ADD: {
    name: 'prescription_incomplete_posology_add_btn_click',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_OPEN_WINDOW: {
    name: 'prescription_voice_open_window',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_REDUCE_WINDOW: {
    name: 'prescription_voice_reduce_window',
    type: EventType.CLICK,
  },
  PRESCRIPTION_ITEM_REMOVE: {
    name: 'prescription_item_remove',
    type: EventType.CLICK,
  },
  PRESCRIPTION_ITEM_EDIT: {
    name: 'prescription_item_edit',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_CLOSE_WINDOW: {
    name: 'prescription_voice_close_window',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_HINT_TOGGLE: {
    name: 'prescription_voice_hint_toggle',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_SWITCH_MICROPHONE: {
    name: 'prescription_voice_switch_microphone',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_PERMISSION_DENIED_RETRY: {
    name: 'prescription_voice_permission_denied_retry',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_ENABLE_MICROPHONE: {
    name: 'prescription_voice_enable_microphone',
    type: EventType.CLICK,
  },
  PRESCRIPTION_VOICE_TRANSACTION_SUCCESS: {
    name: 'prescription_voice_transaction_success',
    type: EventType.TRANSACTIONAL,
  },
  OFFICE_SIGN_UP_SIDEBAR_CLICK: {
    name: 'CREDENTIALS',
    type: EventType.CLICK,
  },
  OFFICE_SEND_SIGNUP_PHONE_NUMBER_CLICK: {
    name: 'SEND',
    type: EventType.CLICK,
  },
  OFFICE_CLOSE_SIDEBAR_CLICK: {
    name: 'CLOSE',
    type: EventType.CLICK,
  },
  OFFICE_GO_BACK_CLICK: {
    name: 'BACK',
    type: EventType.CLICK,
  },
  OFFICE_SWITCH_NUMBER_CLICK: {
    name: 'SWITCH',
    type: EventType.CLICK,
  },
  OFFICE_RESEND_WHATSAPP_LINK_CLICK: {
    name: 'RESEND',
    type: EventType.CLICK,
  },
  OFFICE_CONFIRM_PHONE_NUMBER_CLICK: {
    name: 'CONFIRM',
    type: EventType.CLICK,
  },
  OFFICE_ALTER_PHONE_NUMBER_CLICK: {
    name: 'SWITCH',
    type: EventType.CLICK,
  },
  CONSENT_DIALOG_CLOSE: {
    name: 'CLOSE',
    type: EventType.CLICK,
  },
  CONSENT_DIALOG_LATER: {
    name: 'LATER',
    type: EventType.CLICK,
  },
  CONSENT_DIALOG_CONSENT: {
    name: 'CONSENT',
    type: EventType.CLICK,
  },
} as const;

/**
 * Tipo auxiliar para garantir type-safety nos nomes dos eventos
 */
export type TrackedEventName = (typeof TRACKED_EVENTS)[keyof typeof TRACKED_EVENTS]['name'];
